import { useState, useEffect } from "react";
import Modal from "../components/Modal";
import EditUser from "../components/user/EditUser";
import AddUser from "../components/user/AddUser";
import api from "../utils/api";
import AddBtn from "../components/AddBtn";
import EditBtn from "../components/EditBtn";
import DeleteBtn from "../components/DeleteBtn";
import RoleAccess from "../components/RoleAccess";

export default function User() {
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [isModalOpenForAdd, setIsModalOpenForAdd] = useState(false);
  const [isModalOpenForEdit, setIsModalOpenForEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const itemsPerPage = 5;

  const productMap = products.reduce((acc, product) => {
    acc[product.name] = product;
    return acc;
  }, {});

  const fetchUsers = async (page = 1, q = "") => {
    setLoading(true);
    try {
      const res = await api.get("/users", {
        params: { page, limit: itemsPerPage, q },
        withCredentials: true,
      });

      setUsers(res.data.users || res.data || []);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.totalPages || 1);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(currentPage, search);
  }, [currentPage, search]);

  useEffect(() => {
    api
      .get("/users/me", { withCredentials: true })
      .then((res) => setUserRole(res.data.role))
      .catch(() => setUserRole(null));
  }, []);

  useEffect(() => {
    api
      .get("/products", { withCredentials: true })
      .then((res) => {
        setProducts(res.data.products);
      })
      .catch((err) => console.error("Product fetch error:", err));
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch(searchInput);
  };

  const handleClear = () => {
    setSearchInput("");
    setCurrentPage(1);
    setSearch("");
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await api.delete(`/users/${id}`, { withCredentials: true });

      if (users.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        setUsers((prev) => prev.filter((u) => u._id !== id));
      }
    } catch (err) {
      console.error(err);
      alert("Failed to delete user!");
    }
  };

  return (
    <>
      <Modal
        isOpen={isModalOpenForAdd}
        onClose={() => setIsModalOpenForAdd(false)}
      >
        <AddUser
          onSuccess={async () => {
            setIsModalOpenForAdd(false);
            setCurrentPage(1);
            fetchUsers(1, search);
          }}
        />
      </Modal>
      <Modal
        isOpen={isModalOpenForEdit}
        onClose={() => setIsModalOpenForEdit(false)}
      >
        {editingUser && (
          <EditUser
            user={editingUser}
            onSuccess={async () => {
              setIsModalOpenForEdit(false);
              fetchUsers(currentPage, search);
            }}
          />
        )}
      </Modal>
      <div>
        <h1 className="text-2xl font-bold mt-10 ml-2 mb-3">User List</h1>

        <div className="ml-2 mt-5 bg-white rounded-md shadow-lg p-5">
          <RoleAccess allow={["Admin", "Manager"]} role={userRole}>
            <div className="mb-4">
              <AddBtn
                onClick={() => setIsModalOpenForAdd(true)}
                label="Add User"
              />
            </div>
          </RoleAccess>
          <div className="mb-4 flex justify-end gap-2">
            <input
              type="text"
              placeholder="Search by username, email, or role..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              className="w-[450px] px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition"
            >
              Search
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition"
            >
              Clear
            </button>
          </div>
          <table className="w-full text-sm border-separate border-spacing-y-2">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                <th className="px-4 py-3 text-left rounded-tl-xl rounded-bl-xl w-1/6">
                  Username
                </th>
                <th className="px-4 py-3 text-center">Email</th>
                <th className="px-4 py-3 text-center">Role</th>
                <th className="px-4 py-3 text-center">Products</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 pr-6 py-3 text-right rounded-tr-xl rounded-br-xl">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    Loading users...
                  </td>
                </tr>
              ) : users.length ? (
                users.map((u) => (
                  <tr key={u._id} className="transition hover:bg-slate-100">
                    <td className="px-4 py-3 text-left">{u.username}</td>
                    <td className="px-4 py-3 text-center">{u.email}</td>
                    <td className="px-4 py-3 text-center">{u.role}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex gap-2 justify-center flex-wrap">
                        {u.product_list?.map((productName) => {
                          const product = productMap[productName];
                          if (!product) return null;

                          return (
                            <img
                              key={productName}
                              src={`${process.env.REACT_APP_API_URL}/${product.imageUrl}`}
                              alt={productName}
                              title={productName}
                              className="h-8 w-8 rounded object-cover border"
                            />
                          );
                        })}
                      </div>
                    </td>

                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center justify-center rounded-full px-3 py-2 text-xs font-medium text-white
                        ${u.status === "Active" ? "bg-green-500" : "bg-red-600"}
                        `}
                      >
                        {u.status}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-4">
                        <EditBtn
                          onClick={() => {
                            setEditingUser(u);
                            setIsModalOpenForEdit(true);
                          }}
                          label="Edit User"
                        />
                        <DeleteBtn
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteUser(u._id);
                          }}
                          label="Delete User"
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-4">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>
              <span className="px-3 py-1 font-medium">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
